import { defineStore } from "pinia"
import { useExecutionStore } from "./index"

// A Broker edits its Company's bank list server-side and the Company user gets
// no signal that it changed, so the record is re-read on a timer.
export const COMPANY_REFRESH_INTERVAL_MS = 5 * 1000

// The record stores a bank's override flag as `isOverride`; the UI reads it as
// `overrideMode`. Translating in both directions keeps a save from dropping it.
function fromRecord (banklist) {
  return (banklist || []).map(bank => ({
    name: bank.name,
    id: bank.id,
    selected: bank.selected,
    limit: bank.limit,
    status: bank.status,
    timeDue: bank.timeDue,
    overrideMode: bank.isOverride,
    email: bank.email
  }))
}

function toRecord (banklist) {
  return banklist.map(bank => ({
    name: bank.name,
    id: bank.id,
    selected: bank.selected,
    limit: bank.limit,
    status: bank.status,
    timeDue: bank.timeDue,
    isOverride: bank.overrideMode,
    email: bank.email
  }))
}

export const useCompanyStore = defineStore("company", {
  state: () => ({
    banklist: [],
    profile: null,
    inFlight: null
  }),
  getters: {
    // The role cannot change without a reload, so localStorage is enough.
    isPro: () => String(localStorage.getItem('userRole') || '').toLowerCase() === 'pro',
    selectedBanks: state => state.banklist.filter(bank => bank.selected)
  },
  actions: {
    applyCompany (company) {
      if (!company) return null
      this.banklist = fromRecord(company.banklist)
      this.profile = {
        companyName: company.name,
        companyRepresentative: localStorage.getItem('userName'),
        email: localStorage.getItem('userEmail'),
        cnpj: company.cnpj,
        phone: company.phone,
        region: company.region
      }
      return company
    },
    // ToolBar and the poller both ask on boot, in an order the layout's template
    // decides. Sharing the request means both get the same answer either way.
    fetchCompany () {
      if (!this.isPro) return Promise.resolve(null)
      const companyId = localStorage.getItem('companyId')
      if (!companyId || companyId.length <= 10) return Promise.resolve(null)
      if (this.inFlight) return this.inFlight

      this.inFlight = this.requestCompany(companyId)
      return this.inFlight
    },
    async requestCompany (companyId) {
      try {
        const response = await useExecutionStore().getCompanyById(companyId)
        return this.applyCompany(response.data)
      } catch (error) {
        console.log(error)
        return null
      } finally {
        this.inFlight = null
      }
    },
    // The draft holds only the two fields the settings form owns, so a bank the
    // Broker granted while the form was open is not dropped by the save. The
    // store takes the list only once the backend has, so a refused save never
    // reaches the screens that quote with it.
    async saveBanklist (draft) {
      const edits = new Map(draft.map(bank => [bank.id, bank]))
      const banklist = this.banklist.map(bank => {
        const edit = edits.get(bank.id)
        return edit ? { ...bank, selected: edit.selected, limit: edit.limit } : bank
      })

      await useExecutionStore().putCompany({
        id: localStorage.getItem('companyId'),
        banklist: toRecord(banklist)
      })
      this.banklist = banklist
    }
  }
})
